import './index.css';
import { useState, useEffect } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import Application from './pages/Application';
import Home from './pages/Home';
import LeftNavMenu from './components/Navigation/LeftNavMenu';
import SystemE2E from './pages/SystemE2E';
import Tag from './pages/Tag';
import TopBar from './components/TopBar/TopBar';
import MobileMonitoring from './pages/mobile/Monitoring';
import MobileQuarantine from './pages/mobile/Quarantine';
import MobileSdkPerformance from './pages/mobile/SdkPerformance';
import ApplicationTestMonitoring from './pages/application/TestMonitoring';
import TagTestMonitoring from './pages/tag/TestMonitoring';
import Login from './pages/Login';
import Campaigns from './pages/Campaigns';
import QualityKpi from './pages/QualityKpi';
import Admin from './pages/Admin';
import AdminCampaigns from './components/admin/AdminCampaigns';
import AdminTeamMembers from './components/admin/AdminTeamMembers';
import AdminTestManagement from './components/admin/AdminTestManagement';
import AdminMailForwarderRules from './components/admin/AdminMailForwarderRules';
import AdminMailForwarderLog from './components/admin/AdminMailForwarderLog';
import AdminBlockedSenders from './components/admin/AdminBlockedSenders';
import RoleGate from './components/RoleGate';
import TranslationAnomaly from './pages/TranslationAnomaly';
import UserIdHasher from './pages/data/UserIdHasher';
import TestsManagement from './pages/quality/TestsManagement';
import SoftwareTestReport from './pages/quality/SoftwareTestReport';
import EvaluationRuns from './pages/senseShield/EvaluationRuns';
import FeedbackLoop from './pages/senseShield/FeedbackLoop';
import Summary from './pages/senseShield/Summary';
import DuplicateTickets from './pages/bug-management/DuplicateTickets';
import Profile from './pages/Profile';
import DemoEnvMgmt from './pages/demo-env-mgmt/page';
import api from './api';
import { QUALITY_SERVICE_API_URL } from './config';
import LoginCallback from './pages/LoginCallback';
import CurrentUserContext from './context/CurrentUserContext';
import { getDemoEnvMgmtAccess } from './utils/demoEnvMgmtApi';

function App() {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login' || location.pathname === '/' || location.pathname === '/login/callback';

  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [demoEnvMgmtAuthorized, setDemoEnvMgmtAuthorized] = useState(false);

  useEffect(() => {
    if (isLoginPage) {
      setAuthLoading(false);
      return;
    }

    // Provision/fetch the hub user record (also validates the token via AuthGuard).
    api.get(`${QUALITY_SERVICE_API_URL}/hub/users/me`)
      .then(res => {
        setCurrentUser(res.data);
        setAuthLoading(false);
      })
      .catch(() => {
        // 401/403: the response interceptor in api.js handles the redirect to /login.
        setAuthLoading(false);
      });

    // Resolved in parallel with the call above — drives both the nav item's visibility and the
    // /demo-env-mgmt route gate. Defaults to false (not authorized) on any error.
    getDemoEnvMgmtAccess()
      .then(setDemoEnvMgmtAuthorized)
      .catch(() => setDemoEnvMgmtAuthorized(false));
  }, [isLoginPage]); // eslint-disable-line react-hooks/exhaustive-deps

  if (authLoading) return null;

  if (!isLoginPage && !currentUser) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }

  const userEmail = currentUser?.email ?? null;

  let userInitials = 'UU';
  if (currentUser?.firstName && currentUser?.lastName) {
    userInitials = (currentUser.firstName[0] + currentUser.lastName[0]).toUpperCase();
  } else if (userEmail) {
    const parts = userEmail.split('@')[0].split('.');
    if (parts.length >= 2) {
      userInitials = (parts[0][0] + parts[1][0]).toUpperCase();
    }
  }

  return (
    <CurrentUserContext.Provider value={currentUser}>
      <div className="App">
        {!isLoginPage && (
          <>
            <TopBar userInitials={userInitials} currentUser={currentUser} onUserUpdate={setCurrentUser} />
            <div className="main-content">
              <LeftNavMenu showDemoEnvMgmt={demoEnvMgmtAuthorized} />
              <Routes>
                <Route path="/home" element={<Home userEmail={userEmail} />} />
                <Route path="/application" element={<Application />} />
                <Route path="/application/tests-monitoring" element={<ApplicationTestMonitoring />} />
                <Route path="/mobile/tests-monitoring" element={<MobileMonitoring />} />
                <Route path="/mobile/tests-quarantine" element={<MobileQuarantine />} />
                <Route path="/mobile/sdk-performance" element={<MobileSdkPerformance />} />
                <Route path="/tag" element={<Tag />} />
                <Route path="/tag/tests-monitoring" element={<TagTestMonitoring />} />
                <Route path="/system-e2e" element={<SystemE2E />} />
                <Route path="/quality/campaigns" element={<Campaigns />} />
                <Route path="/campaigns/:campaignId" element={<Campaigns />} />
                <Route path="/quality/kpi-dashboard" element={<QualityKpi />} />
                <Route path="/admin" element={<Admin />}>
                  <Route index element={<Navigate to="campaigns" replace />} />
                  <Route path="campaigns" element={<AdminCampaigns />} />
                  <Route path="campaigns/:id" element={<AdminCampaigns />} />
                  <Route path="team" element={<AdminTeamMembers />} />
                  <Route path="team/:id" element={<AdminTeamMembers />} />
                  <Route path="tests-management" element={<AdminTestManagement />} />
                  <Route path="mail-forwarders" element={
                    <RoleGate allow={['admin', 'write']} fallback={<Navigate to="/admin/campaigns" replace />}>
                      <AdminMailForwarderRules />
                    </RoleGate>
                  } />
                  <Route path="mail-forwarders/:id" element={
                    <RoleGate allow={['admin', 'write']} fallback={<Navigate to="/admin/campaigns" replace />}>
                      <AdminMailForwarderRules />
                    </RoleGate>
                  } />
                  <Route path="mail-forwarders/blocked-senders" element={
                    <RoleGate allow={['admin', 'write']} fallback={<Navigate to="/admin/mail-forwarders" replace />}>
                      <AdminBlockedSenders />
                    </RoleGate>
                  } />
                  <Route path="mail-forwarders/log" element={
                    <RoleGate allow={['admin']} fallback={<Navigate to="/admin/mail-forwarders" replace />}>
                      <AdminMailForwarderLog />
                    </RoleGate>
                  } />
                </Route>
                <Route path="/data/user-id-hasher" element={<UserIdHasher />} />
                <Route path="/quality/tests-management" element={<TestsManagement />} />
                <Route path="/quality/software-test-report" element={<SoftwareTestReport />} />
                <Route path="/sense-shield/summary" element={<Summary />} />
                <Route path="/sense-shield/evaluation-runs" element={<EvaluationRuns />} />
                <Route path="/sense-shield/feedback-loop" element={<FeedbackLoop />} />
                <Route path="/bug-management/duplicate-tickets" element={<DuplicateTickets />} />
                <Route path="/translation-anomaly" element={<TranslationAnomaly />} />
                <Route path="/profile" element={<Profile currentUser={currentUser} onUserUpdate={setCurrentUser} />} />
                <Route path="/demo-env-mgmt" element={demoEnvMgmtAuthorized ? <DemoEnvMgmt /> : <Navigate to="/home" replace />} />
              </Routes >
            </div >
          </>
        )
        }
        {
          isLoginPage && (
            <Routes>
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/login" element={<Login />} />
              <Route path="/login/callback" element={<LoginCallback />} />
            </Routes>
          )
        }
      </div >
    </CurrentUserContext.Provider>
  );
}

export default App;
